import { keyBy } from 'lodash';
import { SalesforceActions, SalesforceAutomation } from 'api/integrations';
import { getApiFilterTransformer } from 'components/pages/api-filter-transformer';
import {
  useSalesForceActionFieldsQuery,
  useSalesForceMatchFieldsQuery,
  useSalesForceTriggerFieldsQuery,
  useSalesForceTriggerObjectsQuery,
  useTemplatePlaceholders,
} from 'queries/salesforce-integration';
import { convertIntegrationFieldToField } from 'utils/fields-conversion';

const EmptySalesforceObject = '__EMPTY__';

export const useAutomationData = (
  integrationId: number,
  salesforceObject?: string,
  defaultAutomationData?: SalesforceAutomation,
  setAutomationDetails?: (key: string, value: any) => void,
) => {
  const _salesforceObject = salesforceObject ?? EmptySalesforceObject;

  // Salesforce Object
  const { data: salesforceObjects, status: salesforceObjectsStatus } =
    useSalesForceTriggerObjectsQuery(integrationId, {
      onSuccess: (resp) => {
        if (defaultAutomationData && setAutomationDetails) {
          const selectedSalesforceObject = resp.find(
            (sfObject) => sfObject.id === defaultAutomationData.object,
          );

          if (selectedSalesforceObject) {
            setAutomationDetails('salesforceObject', {
              value: selectedSalesforceObject?.id,
              label: selectedSalesforceObject?.name,
            });
          }
        }
      },
    });

  // Salesforce Triggers
  const {
    data: salesforceTriggerFields = [],
    status: salesforceTriggerFieldsStatus,
  } = useSalesForceTriggerFieldsQuery(integrationId, _salesforceObject, {
    enabled: _salesforceObject !== EmptySalesforceObject,
    onSuccess: (resp = []) => {
      if (
        defaultAutomationData &&
        defaultAutomationData?.triggerCondition &&
        defaultAutomationData.object === _salesforceObject
      ) {
        const lookup = keyBy(convertIntegrationFieldToField(resp), 'fieldId');
        const transform = getApiFilterTransformer(lookup);

        setAutomationDetails?.(
          'triggers',
          transform.from(defaultAutomationData?.triggerCondition),
        );
      }
    },
  });

  // Salesforce Matches
  const {
    data: salesforceMatchFields = [],
    status: salesforceMatchFieldsStatus,
  } = useSalesForceMatchFieldsQuery(integrationId, _salesforceObject, {
    enabled: _salesforceObject !== EmptySalesforceObject,
    onSuccess: (resp = []) => {
      if (
        defaultAutomationData &&
        defaultAutomationData?.matchingCondition &&
        defaultAutomationData.object === _salesforceObject
      ) {
        const lookup = keyBy(convertIntegrationFieldToField(resp), 'fieldId');
        const transform = getApiFilterTransformer(lookup);

        setAutomationDetails?.(
          'matches',
          transform.from(defaultAutomationData?.matchingCondition),
        );
      }
    },
  });

  const {
    data: templatePlaceholderFields = [],
    status: templatePlaceholderFieldsStatus,
  } = useTemplatePlaceholders(
    integrationId,
    _salesforceObject,
    defaultAutomationData?.actions[0].inputs.templateId,
    {
      enabled: _salesforceObject !== EmptySalesforceObject,
    },
  );
  // Salesforce Actions
  const {
    data: salesforceActionFields = [],
    status: salesforceActionFieldsStatus,
  } = useSalesForceActionFieldsQuery(
    integrationId,
    _salesforceObject,
    SalesforceActions.Project,
    {
      enabled:
        _salesforceObject !== EmptySalesforceObject &&
        (defaultAutomationData?.actions[0].inputs.templateId === null ||
          (defaultAutomationData?.actions[0].inputs.templateId !== null &&
            templatePlaceholderFieldsStatus !== 'loading')),
      onSuccess: (resp) => {
        if (
          defaultAutomationData &&
          setAutomationDetails &&
          defaultAutomationData.object === _salesforceObject
        ) {
          const newData = [...templatePlaceholderFields, ...resp];
          const selectedData = Object.keys(
            defaultAutomationData.actions?.[0].inputs,
          ).reduce((acc, each) => {
            const currentField = newData.find((r) => r.fieldName === each);

            if (currentField) {
              const val = defaultAutomationData.actions?.[0].inputs[each];

              if (currentField.fieldName === 'teamMembers') {
                const teamMemberData = defaultAutomationData.actions?.[0]
                  .inputs[each] as string;

                const template: any = [];

                teamMemberData
                  ?.toString()
                  ?.split(',')
                  .forEach((item) => {
                    const member = currentField?.metaData?.options?.find(
                      (o) => o.valueStr === item,
                    );

                    if (member) {
                      template.push(member);
                    }
                  });

                return {
                  ...acc,
                  [each]: template,
                };
              }
              const rlFieldValue = currentField?.metaData?.options?.find(
                (o) => o.valueStr === val,
              );

              return {
                ...acc,
                [each]: {
                  label: rlFieldValue?.label,
                  valueStr: rlFieldValue?.valueStr,
                  valueMeta: rlFieldValue?.valueMeta,
                },
              };
            }

            return acc;
          }, {});

          setAutomationDetails('rocketlaneFields', selectedData);
        }
      },
    },
  );
  const salesForceActionFieldMergedData = [
    ...salesforceActionFields,
    ...templatePlaceholderFields,
  ];

  return {
    salesforceObjects,
    salesforceObjectsStatus,
    salesforceTriggerFields,
    salesforceTriggerFieldsStatus,
    salesforceMatchFields,
    salesforceMatchFieldsStatus,
    salesforceActionFields,
    salesForceActionFieldMergedData,
    salesforceActionFieldsStatus,
  };
};
